/**
 * Layout Component
 *
 * @fileoverview Authenticated app shell — fixed sidebar on md+, slim topbar
 * with breadcrumb / actions, mobile drawer for the sidebar.
 *
 * @changeHistory
 * - 2026-10-01: Shared broker/admin navigation and tracked workspace view changes (Codex)
 * - 2025-11-15: Added documentation and resolved lint formatting for navigation (GitHub Copilot)
 * - 2025-12-06: Integrated Header component matching Figma design
 * - 2026-05-09: Switched to sidebar + slim topbar app shell with mobile drawer (Claude)
 * - 2026-07-23: Added Team navigation for admins managing leadership metadata (Codex)
 * - 2026-08-13: Restored Transactions and Reporting navigation for admins (Codex)
 */

import { Fragment, useMemo, useState, type JSX, type ReactNode } from 'react';

import { Link } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import { useAuthStore } from '@shared/stores/auth.store';
import { CLIENT_EVENTS, track } from '@shared/analytics/events';
import { authApi } from '../../services/api-client';

import AppSidebar, { type NavItem } from './AppSidebar';
import BrokerAgentSwitcher from './BrokerAgentSwitcher';
import Topbar from './Topbar';
import './Layout.css';

interface LayoutProps {
  children: ReactNode;
}

const Layout = ({ children }: LayoutProps): JSX.Element => {
  const { user, logout } = useAuthStore();
  const [brokerViewMode, setBrokerViewMode] = useState<'admin' | 'agent'>('admin');

  const handleLogout = (): void => {
    authApi
      .logout()
      .catch((error) => {
        console.error('Logout error:', error);
      })
      .finally(() => {
        logout();
      });
  };

  const navigation = useMemo<NavItem[]>(() => {
    if (user?.role === 'agent') {
      return [
        { name: 'Dashboard', href: '/dashboard' },
        { name: 'Submit CDA', href: '/submit' },
        { name: 'Listings', href: '/listings' },
        { name: 'Blog', href: '/blog-posts' },
        { name: 'Title Companies', href: '/title-companies' },
      ];
    }
    if (user?.role === 'broker' || user?.role === 'admin') {
      const adminNavigation: NavItem[] =
        user.role === 'admin' ? [{ name: 'Diagnostics', href: '/admin/diagnostics' }] : [];
      // Broker dual-role: show either admin or agent navigation based on mode (US8)
      if (brokerViewMode === 'agent') {
        return [
          { name: 'Dashboard', href: '/dashboard' },
          { name: 'Submit CDA', href: '/submit' },
          { name: 'Listings', href: '/listings' },
          { name: 'Blog', href: '/blog-posts' },
          { name: 'Title Companies', href: '/title-companies' },
          ...adminNavigation,
        ];
      }
      return [
        { name: 'Team', href: '/team' },
        { name: 'Transactions', href: '/transactions' },
        { name: 'Listings', href: '/listings' },
        { name: 'Blog', href: '/blog-posts' },
        { name: 'Title Companies', href: '/title-companies' },
        { name: 'Reporting', href: '/reporting' },
        { name: 'Comp Plans', href: '/compensation-plans' },
        { name: 'Audit Logs', href: '/audit-logs' },
        ...adminNavigation,
      ];
    }
    if (user?.role === 'transaction_coordinator') {
      return [
        { name: 'Transactions', href: '/transactions' },
        { name: 'Submit CDA', href: '/submit' },
        { name: 'Title Companies', href: '/title-companies' },
      ];
    }
    return [];
  }, [user?.role, brokerViewMode]);

  const switcher =
    user?.role === 'broker' || user?.role === 'admin' ? (
      <BrokerAgentSwitcher
        currentMode={brokerViewMode}
        onModeChange={(mode) => {
          if (mode !== brokerViewMode) {
            track(CLIENT_EVENTS.WORKSPACE_VIEW_CHANGED, { role: user.role, mode });
            setBrokerViewMode(mode);
          }
        }}
      />
    ) : undefined;

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-950">
      <AppSidebar navigation={navigation} onLogout={handleLogout} />

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar navigation={navigation} switcher={switcher} onLogout={handleLogout} />

        <main className="w-full flex-1">
          <Transition
            as={Fragment}
            show
            enter="transition-opacity duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">{children}</div>
          </Transition>
        </main>

        <footer className="mb-6 flex flex-col items-center justify-between gap-4 p-6 text-xs text-slate-400 dark:border-slate-800 dark:text-slate-500 md:flex-row md:px-8">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Copyright © {new Date().getFullYear()} Cloud Team Realty
          </p>
          <div className="flex gap-6">
            <Link
              to="/privacy"
              className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
            >
              Terms and Conditions
            </Link>
            <Link
              to="/contact"
              className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
            >
              Contact
            </Link>
            <Link
              to="/status"
              className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
            >
              System Status
            </Link>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Layout;
